// src/services/data/hooks/useCategoryMetrics.ts
/**
 * Unified Category/Sector Metrics Hooks
 * 
 * Low-level hooks for fetching category metric data.
 * Used by both SectorDataProviders and assistant charts.
 */

import { useQuery } from '@tanstack/react-query';
import { 
  fetchCategoryCurrent,
  fetchCategoryCurrentChanges,
  fetchCategoryHistoryCompact,
} from '@/services/queries/dashboards/categoriesQueries';
import { categoryDataKeys } from '../queryKeys';
import { TIME } from '@/constants/app';
import { API_BASE_URL } from '@/services';
import type { CategoryFlat } from '@/types/dashboards/categories';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface CategoryMetricResult {
  categoryId: number;
  categoryCode: string;
  value: number | null;
}

export interface CategoryHistoryResult {
  categoryId: number;
  categoryCode: string;
  dates: (string | number)[];
  values: (number | null)[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Shared Category ID Map Cache (for use outside React components)
// ─────────────────────────────────────────────────────────────────────────────

let categoryIdMapCache: Map<string, number> | null = null;

/**
 * Fetch the category ID map. Cached globally for performance.
 * Use this in async functions (e.g., inside useQuery's queryFn).
 * For React components, prefer useCategoryIdMap() hook.
 */
export async function getCategoryIdMap(): Promise<Map<string, number>> {
  if (categoryIdMapCache) return categoryIdMapCache;
  
  const res = await fetch(`${API_BASE_URL}/categories`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const categories: CategoryFlat[] = await res.json();
  
  categoryIdMapCache = new Map(
    categories.map((c) => [c.category_code.toLowerCase(), c.category_id])
  );
  return categoryIdMapCache;
}

// ─────────────────────────────────────────────────────────────────────────────
// Category ID Mapping Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get a map of category codes to category IDs.
 * This is cached and shared across all category queries.
 */
export function useCategoryIdMap() {
  return useQuery({
    queryKey: categoryDataKeys.idMap(),
    queryFn: getCategoryIdMap,
    staleTime: 60 * TIME.MINUTE, // Categories rarely change
    gcTime: 120 * TIME.MINUTE,
  });
}

/**
 * Resolve category codes to IDs.
 * Returns null if the map isn't loaded yet.
 */
export function useCategoryIds(categoryCodes: string[]) {
  const { data: idMap, isLoading } = useCategoryIdMap();
  
  if (isLoading || !idMap) {
    return { categoryIds: null, isLoading: true };
  }
  
  const categoryIds = categoryCodes
    .map(code => idMap.get(code.toLowerCase()))
    .filter((id): id is number => id !== undefined);
  
  return { categoryIds, isLoading: false };
}

// ─────────────────────────────────────────────────────────────────────────────
// Current Values Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch current metric values for categories.
 */
export function useCategoryCurrentMetric(params: {
  categoryCodes: string[];
  metricKey: string;
  enabled?: boolean;
}) {
  const { categoryCodes, metricKey, enabled = true } = params;
  const { categoryIds, isLoading: idsLoading } = useCategoryIds(categoryCodes);
  
  return useQuery({
    queryKey: categoryDataKeys.current({ 
      categoryIds: categoryIds ?? [] 
    }),
    queryFn: async () => {
      const allCurrent = await fetchCategoryCurrent({});
      
      return allCurrent
        .filter(c => categoryIds?.includes(c.category_id))
        .map((c): CategoryMetricResult => ({
          categoryId: c.category_id,
          categoryCode: categoryCodes.find((_, idx) => categoryIds?.[idx] === c.category_id) ?? '',
          value: c.metrics[metricKey] ?? null,
        }));
    },
    enabled: enabled && !idsLoading && categoryIds !== null && categoryIds.length > 0,
    staleTime: 5 * TIME.MINUTE,
    gcTime: 10 * TIME.MINUTE,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Change Values Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch percent change values for categories over a window.
 */
export function useCategoryChangeMetric(params: {
  categoryCodes: string[];
  metricKey: string;
  window: string;
  enabled?: boolean;
}) {
  const { categoryCodes, metricKey, window, enabled = true } = params;
  const { categoryIds, isLoading: idsLoading } = useCategoryIds(categoryCodes);
  
  return useQuery({
    queryKey: categoryDataKeys.changes({ categoryIds: categoryIds ?? [] }),
    queryFn: async () => {
      const allChanges = await fetchCategoryCurrentChanges({});
      
      return allChanges
        .filter(c => categoryIds?.includes(c.category_id))
        .map((c): CategoryMetricResult => ({
          categoryId: c.category_id,
          categoryCode: categoryCodes.find((_, idx) => categoryIds?.[idx] === c.category_id) ?? '',
          value: c.metrics[metricKey]?.[window]?.value ?? null,
        }));
    },
    enabled: enabled && !idsLoading && categoryIds !== null && categoryIds.length > 0,
    staleTime: 5 * TIME.MINUTE,
    gcTime: 10 * TIME.MINUTE,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Historical Data Hook
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Fetch historical time series for a single category.
 */
export function useCategoryHistory(params: {
  categoryCode: string;
  metricKeys: string[];
  enabled?: boolean;
}) {
  const { categoryCode, metricKeys, enabled = true } = params;
  const { data: idMap, isLoading: idsLoading } = useCategoryIdMap();
  const categoryId = idMap?.get(categoryCode.toLowerCase());
  
  return useQuery({
    queryKey: categoryDataKeys.history({ 
      categoryId: categoryId ?? 0, 
      metricKeys 
    }),
    queryFn: async () => {
      if (!categoryId) return null;
      
      const result = await fetchCategoryHistoryCompact({
        categoryId,
        metricKeys,
      });
      
      return result.metrics?.map((m) => ({
        metricKey: m.metric_key,
        dates: m.dates ?? [],
        values: m.values ?? [],
      })) ?? [];
    },
    enabled: enabled && !idsLoading && categoryId !== undefined,
    staleTime: 5 * TIME.MINUTE,
    gcTime: 10 * TIME.MINUTE,
  });
}

/**
 * Fetch historical time series for multiple categories.
 * Returns aligned data for all categories.
 */
export function useCategoriesHistory(params: {
  categoryCodes: string[];
  metricKey: string;
  enabled?: boolean;
}) {
  const { categoryCodes, metricKey, enabled = true } = params;
  const { data: idMap, isLoading: idsLoading } = useCategoryIdMap();
  
  // Get all category IDs
  const categoryIdsAndCodes = categoryCodes
    .map(code => ({
      code,
      id: idMap?.get(code.toLowerCase()),
    }))
    .filter((item): item is { code: string; id: number } => item.id !== undefined);
  
  return useQuery({
    queryKey: categoryDataKeys.history({ 
      categoryId: categoryIdsAndCodes[0]?.id ?? 0,
      metricKeys: [metricKey],
    }),
    queryFn: async () => {
      const results = await Promise.all(
        categoryIdsAndCodes.map(async ({ code, id }) => {
          const result = await fetchCategoryHistoryCompact({
            categoryId: id,
            metricKeys: [metricKey],
          });
          
          const metricData = result.metrics?.find(m => m.metric_key === metricKey);
          
          return {
            categoryCode: code,
            categoryId: id,
            dates: metricData?.dates?.map(d => String(d)) ?? [],
            values: (metricData?.values ?? []).map(v => v ?? null),
          } as CategoryHistoryResult;
        })
      );
      
      return results;
    },
    enabled: enabled && !idsLoading && categoryIdsAndCodes.length > 0,
    staleTime: 5 * TIME.MINUTE,
    gcTime: 10 * TIME.MINUTE,
  });
}

